import React, { useEffect, useRef } from 'react';
import 'react-quill/dist/quill.snow.css';
import { Controller, useFormContext, ControllerRenderProps } from 'react-hook-form';
import { Input, Select, PendingUpload } from 'alurkerja-ui';
import SwitchButton from './SwitchButton';
import { MarkdownInput } from '@/components/ui/MarkdownInput';
import RichTextEditor from '@/components/ui/RichTextEditor';
import { SubmitButton, SecondaryButton } from '@/components/ui/Button';

export interface FormField {
  name: string;
  label: string;
  type?: string;
  uploadType?: 'image' | 'file' | 'both';
  options?: { label: string; value: string }[];
  placeholder?: string;
  required?: boolean;
  onFileChange?: (file: File | null) => void;
  colSpan?: number;
  disable?: boolean;
}

interface FormBuilderWithLayoutProps {
  title?: string;
  description?: string;
  fields: FormField[];
  actions?: { label: string; variant?: 'primary' | 'outline'; onClick?: () => void }[];
  onSubmit?: () => void;
  onCancel?: () => void;
  submitLabel?: string;
  cancelLabel?: string;
  initialPublished?: boolean;
  onPublishChange?: (value: boolean) => void;
  showPublishButton?: boolean;
  buttonAlign?: 'left' | 'right' | 'between';
  disable?: boolean;
  autoFocusField?: string;
}

function getUploadValue(fValue: any) {
  if (!fValue) return [];
  if (Array.isArray(fValue)) {
    return fValue.map((v: any) => ({
      name: v.name || v.original_name || v.filename || v.path,
      size: Number(v.size) || 1,
      url: v.url,
    }));
  }
  return [{ name: fValue.name || fValue.original_name || fValue.filename || fValue.path, size: Number(fValue.size) || 1, url: fValue.url }];
}

function getAllowedExtensions(uploadType: FormField['uploadType']): string[] {
  if (uploadType === 'image') return ['jpg', 'jpeg', 'png', 'webp'];
  if (uploadType === 'file') return ['pdf', 'doc', 'docx'];
  return [];
}

function renderFWLFieldInput(field: FormField, f: ControllerRenderProps<any, string>, autoFocusRef: React.RefObject<HTMLInputElement>, autoFocusField?: string) {
  switch (field.type) {
  case 'date':
    return (
      <Input {...f} disabled={field.disable} type="date" className="text-sm w-full" placeholder={field.placeholder || 'Select date'} />
    );
  case 'number':
    return (
      <Input {...f} disabled={field.disable} type="number" className="text-sm w-full" placeholder={field.placeholder || 'Enter number'} />
    );
  case 'select':
    return (
      <Select
        size="sm"
        placeholder={field.placeholder || 'Select option'}
        className="text-sm w-full"
        options={field.options || []}
        value={f.value ? field.options?.find(opt => opt.value === String(f.value)) || null : null}
        onChange={(v: any) => f.onChange(v.value)}
        isDisabled={field.disable}
      />
    );
  case 'textarea':
    return <Input textArea {...f} className="text-sm w-full" placeholder={field.placeholder} />;
  case 'switch':
    return (
      <div className="flex items-center justify-between border border-gray-200 rounded-md p-2">
        <span className="text-sm">{field.label}</span>
        <SwitchButton checked={f.value} onChange={f.onChange} />
      </div>
    );
  case 'upload': {
    const isBothUpload = field.uploadType === 'both';
    const allowedExtension = getAllowedExtensions(field.uploadType);
    return (
      <div>
        <PendingUpload
          disabled={field.disable}
          asFile
          hideDownloadPreviewButton
          multiple={false}
          value={getUploadValue(f.value)}
          onChange={(files: any[]) => {
            const fileObj = files?.[0];
            let file: File | null = null;
            if (fileObj instanceof File) {
              file = fileObj;
            } else if (fileObj?.file instanceof File) {
              file = fileObj.file;
            }
            f.onChange(file);
            field.onFileChange?.(file);
          }}
          allowedExtension={isBothUpload ? [] : allowedExtension}
        />
        <small className="text-gray-500 mt-1 block">
          Supports:{' '}
          {isBothUpload ? (
            <strong>all file types (image, document, BPMN, etc.)</strong>
          ) : (
            <strong>{allowedExtension.join(', ').toUpperCase()}</strong>
          )}{' '}
        </small>
      </div>
    );
  }
  case 'richtext':
    return (
      <RichTextEditor value={f.value || ''} onChange={f.onChange} placeholder={field.placeholder || 'Write something...'} />
    );
  case 'markdown':
    return (
      <MarkdownInput
        value={f.value || ''}
        onChange={f.onChange}
        className="w-full mt-1"
        placeholder={field.placeholder || 'Supports *bold*, _italic_, ~~strike~~, `code`'}
      />
    );
  default:
    return (
      <Input
        disabled={field.disable}
        {...f}
        type={typeof field.type === 'string' ? field.type : 'text'}
        className="text-sm w-full"
        placeholder={field.placeholder || field.label}
        ref={autoFocusField === field.name ? autoFocusRef : undefined}
      />
    );
  }
}

export default function FormBuilderWithLayout({
  description,
  fields,
  onSubmit,
  onCancel,
  submitLabel = 'Save',
  cancelLabel = 'Cancel',
  initialPublished = false,
  onPublishChange,
  showPublishButton = false,
  buttonAlign = 'left',
  autoFocusField,
}: Readonly<FormBuilderWithLayoutProps>) {
  const { control } = useFormContext();
  const autoFocusRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (autoFocusField && autoFocusRef.current) {
      // Delay to ensure the input is rendered
      setTimeout(() => {
        autoFocusRef.current?.focus();
        autoFocusRef.current?.select();
      }, 100);
    }
  }, [autoFocusField]);

  let buttonJustify = 'justify-start';
  if (buttonAlign === 'right') buttonJustify = 'justify-end';
  else if (buttonAlign === 'between') buttonJustify = 'justify-between';

  return (
    <div className="space-y-4 md:mx-1 sm:mx-5">
      <div className="flex justify-between items-start">
        <div>{description && <h1 className="text-lg font-semibold">{description}</h1>}</div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {fields.map(field => (
          <div key={field.name} className={`col-span-${field.colSpan || 1}`}>
            <label className="block mb-1 text-sm font-medium">
              {field.label} {field.required && <span className="text-red-500">*</span>}
            </label>

            <Controller
              name={field.name}
              control={control}
              rules={field.required ? { required: `${field.label} is required` } : undefined}
              render={({ field: f, fieldState: { error } }) => (
                <div>
                  {renderFWLFieldInput(field, f, autoFocusRef, autoFocusField)}
                  {error && <p className="text-xs text-red-500 mt-1">{error.message}</p>}
                </div>
              )}
            />
          </div>
        ))}
      </div>

      {showPublishButton && (
        <Controller
          name="is_published"
          control={control}
          defaultValue={initialPublished}
          render={({ field }) => (
            <div className="flex gap-1 items-center">
              <SwitchButton
                checked={field.value}
                onChange={value => {
                  field.onChange(value);
                  onPublishChange?.(value);
                }}
              />
              <span className="text-sm font-semibold">Publish</span>
            </div>
          )}
        />
      )}

      {(onSubmit || onCancel) && (
        <div
          className={`flex gap-2 mt-6 ${buttonJustify}`}
        >
          {onCancel && <SecondaryButton onClick={onCancel}>{cancelLabel}</SecondaryButton>}
          {onSubmit && (
            <SubmitButton onClick={onSubmit} className="bg-blue-600 hover:bg-blue-700 text-white">
              {submitLabel}
            </SubmitButton>
          )}
        </div>
      )}
    </div>
  );
}
